import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputBase } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type SearchBarProp = {
  searchQuery: string;
  onQueryChange: (newQuery: string) => void;
};

/**
 * Search bar to let user search for a specific mailbox.
 *
 * @param searchQuery search keyword input by user.
 * @param onQueryChange callback to handle when user types any letter in the search bar,
 * so as to update search result in real time.
 *
 * @returns a JSX element representing search bar of mailbox group page.
 */
export const SearchBar = ({ searchQuery, onQueryChange }: SearchBarProp) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as any).isContentEditable
      ) {
        return;
      }
      if (e.key === "/" && e.shiftKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <InputBase
      inputRef={inputRef}
      //   1.2px padding to vertically center serarch icon
      startAdornment={<SearchIcon classname="pb-[1.2px]" />}
      value={searchQuery}
      onChange={(event) => {
        const value = event.target.value;
        onQueryChange(value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.currentTarget.blur();
        }
      }}
      placeholder={t(`Search by name or email address`)}
      className="w-full max-w-xl select-none rounded-[14px] border border-[#CCCCCC] bg-white px-4 py-2 text-black transition-all duration-300"
      inputProps={{
        style: {
          color: "#999999",
        },
      }}
      sx={{
        border: "1px solid #CCC",
        borderRadius: 14,
        px: 2,
        py: 1,

        transition: "border-width 0.3s ease, border-color 0.3s ease",

        "&:focus-within": {
          border: "1px solid var(--primary-color)",
        },
      }}
    />
  );
};

type SearchIconProp = {
  classname?: string;
};

const SearchIcon = ({ classname }: SearchIconProp) => (
  <div className={`flex flex-row items-center ${classname}`}>
    <div className="w-[2px]" />
    <FontAwesomeIcon //
      icon={faMagnifyingGlass as IconProp}
      className="text-[#CCCCCC]"
    />
    <div className="w-[10px]" />
  </div>
);
